import { Toast } from '@/components/Toast';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { fmtDateTime } from '@/lib/format';
import type { EmailRecipient } from '@sklad-sync/shared';
import { type FormEvent, useCallback, useEffect, useState } from 'react';

export function AdminRecipientsPage() {
  const [recipients, setRecipients] = useState<EmailRecipient[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ recipients: EmailRecipient[] }>('/api/admin/recipients');
      setRecipients(data.recipients);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function toggle(r: EmailRecipient) {
    try {
      await apiFetch(`/api/admin/recipients/${r.id}`, {
        method: 'PATCH',
        body: { active: !r.active },
      });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Nepodařilo se uložit', kind: 'error' });
    }
  }

  async function remove(id: number) {
    if (!confirm('Smazat tohoto příjemce?')) return;
    try {
      await apiFetch(`/api/admin/recipients/${id}`, { method: 'DELETE' });
      setToast({ msg: 'Příjemce smazán', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Příjemci reportu</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Adresy, kam chodí denní email se shrnutím (zatím není zapnuto)
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
        >
          Přidat příjemce
        </button>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-bg">
              <Th>Email</Th>
              <Th>Jméno</Th>
              <Th>Aktivní</Th>
              <Th>Přidáno</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody>
            {recipients == null ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                  Načítám…
                </td>
              </tr>
            ) : recipients.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                  Žádní příjemci.
                </td>
              </tr>
            ) : (
              recipients.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-border-light hover:bg-surface-hover transition-colors"
                >
                  <td className="px-5 py-3 text-text">{r.email}</td>
                  <td className="px-5 py-3 text-text">{r.name ?? '—'}</td>
                  <td className="px-5 py-3">
                    <button
                      type="button"
                      onClick={() => toggle(r)}
                      className={`text-[11px] font-semibold px-2 py-0.5 rounded cursor-pointer ${
                        r.active
                          ? 'text-success bg-success-light'
                          : 'text-text-muted bg-bg border border-border'
                      }`}
                    >
                      {r.active ? 'Aktivní' : 'Vypnutý'}
                    </button>
                  </td>
                  <td className="px-5 py-3 text-text-secondary">{fmtDateTime(r.created_at)}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => remove(r.id)}
                      className="text-sm font-semibold text-danger hover:underline cursor-pointer"
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {adding && (
        <AddRecipientModal
          onClose={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            setToast({ msg: 'Příjemce přidán', kind: 'success' });
            await load();
          }}
          onError={(msg) => setToast({ msg, kind: 'error' })}
        />
      )}
      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs font-semibold text-text-muted uppercase tracking-wider px-5 py-3">
      {children}
    </th>
  );
}

function AddRecipientModal({
  onClose,
  onSaved,
  onError,
}: {
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await apiFetch('/api/admin/recipients', {
        method: 'POST',
        body: { email, name: name || undefined },
      });
      onSaved();
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.message : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-5">Nový příjemce</h2>
        <hr className="border-border mb-5" />

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label
              className="text-sm font-medium text-text-secondary block mb-1.5"
              htmlFor="rec-email"
            >
              Email
            </label>
            <input
              id="rec-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
          <div>
            <label
              className="text-sm font-medium text-text-secondary block mb-1.5"
              htmlFor="rec-name"
            >
              Jméno (volitelné)
            </label>
            <input
              id="rec-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>

          <div className="flex gap-3 mt-6">
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Přidat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
